import React, { useCallback, useEffect } from 'react';
import { ACTION_STATUSES } from 'shared/consts';
import { useDispatch, useSelector } from 'react-redux';
import { selectComplete3DSCartError, selectSubmitCartStatus } from 'store/cart/selectors';
import { setCurrentPaymentMethodId, updatePaymentMethod, localUpdatePaymentMethod } from 'store/payment/actions';
import {
  selectDeletePaymentMethodsStatus,
  selectGetPaymentMethodsStatus,
  selectSavePaymentMethodStatus,
  selectUpdatePaymentMethodStatus,
} from 'store/payment/selectors';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  INVOICE_PAYMENT_TYPE_ID,
  CREDIT_PAYMENT_TYPE_ID,
  CREDITS_PAYMENT_TYPE_ID,
  AFTER_PAYMENT_PAYMENT_TYPE_ID,
} from 'shared/consts/payment-method-types';

interface UsePaymentMethodsManagerValues {
  onPressHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  updatePaymentMethodStatus: ACTION_STATUSES;
  getPaymentMethodStatus: ACTION_STATUSES;
  deletePaymentMethodsStatus: ACTION_STATUSES;
  savePaymentMethodStatus: ACTION_STATUSES;
}

interface UsePaymentMethodsManagerProps {
  setErrorPaymentId: React.Dispatch<React.SetStateAction<number>>;
  submittedPaymentMethodId: number;
  currentPaymentError: boolean;
  cartId: string;
}

export const usePaymentMethodsManager = ({
  setErrorPaymentId,
  submittedPaymentMethodId,
  currentPaymentError,
  cartId,
}: UsePaymentMethodsManagerProps): UsePaymentMethodsManagerValues => {
  const dispatch = useDispatch();
  const submitCartStatus = useSelector(selectSubmitCartStatus);
  const complete3DSCartError = useSelector(selectComplete3DSCartError);
  const updatePaymentMethodStatus = useSelector(selectUpdatePaymentMethodStatus);
  const getPaymentMethodStatus = useSelector(selectGetPaymentMethodsStatus);
  const deletePaymentMethodsStatus = useSelector(selectDeletePaymentMethodsStatus);
  const savePaymentMethodStatus = useSelector(selectSavePaymentMethodStatus);
  const { enableHqoMiniappApplePay } = useFlags();

  useEffect(() => {
    if (submitCartStatus === ACTION_STATUSES.REJECTED || complete3DSCartError || currentPaymentError) {
      setErrorPaymentId(submittedPaymentMethodId);
    } else {
      setErrorPaymentId(undefined);
    }
  }, [submitCartStatus, submittedPaymentMethodId, complete3DSCartError, currentPaymentError]);

  const onPressHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const paymentMethodId = event.target.value;
      const virtualPaymentMethodIds = [
        INVOICE_PAYMENT_TYPE_ID,
        CREDIT_PAYMENT_TYPE_ID,
        CREDITS_PAYMENT_TYPE_ID,
        AFTER_PAYMENT_PAYMENT_TYPE_ID,
      ];

      if (enableHqoMiniappApplePay && navigator.userAgent.includes('iPhone')) {
        dispatch(setCurrentPaymentMethodId(paymentMethodId));
        return;
      }

      if (virtualPaymentMethodIds.includes(paymentMethodId)) {
        dispatch(setCurrentPaymentMethodId(paymentMethodId));
        dispatch(localUpdatePaymentMethod(parseInt(paymentMethodId)));
        return;
      }

      // Keep currentPaymentMethodId as the single source of truth for the selected method.
      // The saved-card path persists `default: true` to the backend asynchronously, so we
      // also update the selected id immediately — otherwise the selection would not reflect
      // the click until the request resolves (and consumers reading currentPaymentMethodId
      // would stay on the previous card).
      dispatch(setCurrentPaymentMethodId(paymentMethodId));
      dispatch(
        updatePaymentMethod.request({
          default: true,
          paymentMethodId: parseInt(paymentMethodId),
        }),
      );
    },
    [dispatch, cartId, enableHqoMiniappApplePay, setCurrentPaymentMethodId],
  );

  return {
    onPressHandler,
    updatePaymentMethodStatus,
    getPaymentMethodStatus,
    deletePaymentMethodsStatus,
    savePaymentMethodStatus,
  };
};
